import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { IDataImportService, SubmissionFileModel, SubmissionLockError } from "services/DataImportService";
import { RootState } from "setup";
import { SERVICE_TYPES } from "setup/dependency/ServiceTypes";
import { container } from "setup/dependency/inversify.config";
import { clearActiveSubmission, selectActiveSubmission } from "../../ActiveSubmissionSlice";
import { createSubmission, getActiveSubmission } from "../../../shared/submissionThunks";
import {
  buildSheetMappingsFromUploadState,
  canonicalWorksheetName,
  findSheetMappingsForWorksheet,
  replaceSheetWorksheets,
  SheetMapping,
  toSheetMapping,
  toWorksheetTypeEntry,
} from "../../sheetMappings";
import { TEMPLATE_TYPE_IDS } from "../../../shared/templateData";
import { chooseMainSheet, WorkbookSheetEntry } from "../../sheetSources";
import { clearWorksheetMapping } from "../Mapping/mappingThunks";
import { getSubmissionFile } from "./submissionFileStore";
import { deriveUploadMode, suggestWorksheet, UploadErrorKind, UploadFileStatus, UploadMode } from "./uploadRules";

// Metadata only — the raw File handles live in submissionFileStore (non-serialisable, can't go in Redux).
export type UploadFileEntry = {
  fileName: string;
  fileSizeBytes: number;
  status: UploadFileStatus;
  errorKind?: UploadErrorKind;
  // Server row id, once the file exists server-side (upload response or GetFiles hydration).
  // Needed for DeleteFile; absent while scanning or on client-side rejects.
  submissionFileID?: string;
  // CSV mode only: which template worksheet this file feeds. null = "Not provided".
  targetWorksheet: string | null;
  matchedByFilename: boolean;
};

export type UploadState = {
  mode: UploadMode;
  files: UploadFileEntry[];
  // Excel mode: visible sheets of the uploaded workbook, read client-side once it's scanned.
  // undefined = not read yet (or unreadable — e.g. the raw handle was lost on a hard refresh).
  // Persisted as sheet mappings (SaveSheetMappings) when the Upload step's "Continue" commits the selection.
  sheets?: WorkbookSheetEntry[];
};

type FileUploadState = {
  UploadState: UploadState | null;
  LoadingFiles: boolean;
  // GetFiles has run (or was skipped as pointless) for the active submission — guards the
  // hydration effect against refetch loops when the server has no files.
  FilesHydrated: boolean;
  // Persisted sheet-to-worksheet associations (SaveSheetMappings/GetSheetMappings). Written when the Upload step's
  // "Continue" saves the selection, or hydrated on resume; null = not saved/loaded yet.
  SheetMappings: SheetMapping[] | null;
  // Mapping clears still in flight, so Continue waits for them
  ClearingMappingRequestIDs: string[];
  SheetMappingsHydrated: boolean;
};

const initialState: FileUploadState = {
  UploadState: null,
  LoadingFiles: false,
  FilesHydrated: false,
  SheetMappings: null,
  ClearingMappingRequestIDs: [],
  SheetMappingsHydrated: false,
};

function applyPersistedSheetMappingsToCsvFiles(state: FileUploadState, worksheets: string[]) {
  const sheetMappings = state.SheetMappings;
  const upload = state.UploadState;
  if (!sheetMappings || sheetMappings.length === 0 || !upload || upload.mode !== UploadMode.Csv) return;
  for (const file of upload.files) {
    if (!file.submissionFileID) continue;
    const sheetMapping = sheetMappings.find((item) => item.submissionFileID === file.submissionFileID);
    const worksheet = sheetMapping ? canonicalWorksheetName(sheetMapping.worksheet, worksheets) : null;
    if (worksheet) {
      file.targetWorksheet = worksheet;
      file.matchedByFilename = false;
    }
  }
}

export const fileUploadSlice = createSlice({
  name: "dataImportUpload",
  initialState,
  reducers: {
    // Whole UploadState is replaced, never patched — the component always sends the full list.
    setUploadFiles: (state, action: PayloadAction<UploadState | null>) => {
      state.UploadState = action.payload;
    },
    setUploadFileWorksheet: (state, action: PayloadAction<{ fileName: string; worksheet: string | null }>) => {
      const file = state.UploadState?.files.find((item) => item.fileName === action.payload.fileName);
      if (file) {
        file.targetWorksheet = action.payload.worksheet;
        file.matchedByFilename = false;
      }
    },
    // Excel mode: the component reads the workbook's sheet names (with auto-matched worksheets)
    // and stores the result here. setUploadFiles replaces the whole UploadState, so any batch
    // change clears the sheets and triggers a fresh read.
    setWorkbookSheets: (state, action: PayloadAction<WorkbookSheetEntry[]>) => {
      if (state.UploadState) {
        state.UploadState.sheets = action.payload;
      }
    },
    // Worksheets the sheet already feeds keep their source order and key
    setSheetWorksheets: (state, action: PayloadAction<{ sheetName: string; worksheets: string[] }>) => {
      const { sheetName, worksheets } = action.payload;
      if (state.UploadState?.sheets) {
        state.UploadState.sheets = replaceSheetWorksheets(
          state.UploadState.sheets,
          state.SheetMappings ?? [],
          sheetName,
          worksheets
        );
      }
    },
    setSheetKeyColumn: (
      state,
      action: PayloadAction<{ sheetName: string; worksheet: string; keyColumnName: string | null }>
    ) => {
      const { sheetName, worksheet, keyColumnName } = action.payload;
      const sheet = state.UploadState?.sheets?.find((item) => item.sheetName === sheetName);
      const target = sheet?.targets.find((item) => item.worksheet === worksheet);
      if (target) {
        target.keyColumnName = keyColumnName;
      }
    },
    setMainSheet: (state, action: PayloadAction<{ worksheet: string; sheetName: string }>) => {
      const { worksheet, sheetName } = action.payload;
      if (state.UploadState?.sheets) {
        state.UploadState.sheets = chooseMainSheet(state.UploadState.sheets, worksheet, sheetName);
      }
    },
    clearUpload: (state) => {
      state.UploadState = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Upload state belongs to one submission: reset whenever the active submission changes.
      .addCase(getActiveSubmission.pending, (state) => {
        state.UploadState = null;
        state.LoadingFiles = false;
        state.FilesHydrated = false;
        state.SheetMappings = null;
        state.ClearingMappingRequestIDs = [];
        state.SheetMappingsHydrated = false;
      })
      .addCase(createSubmission.fulfilled, (state) => {
        state.UploadState = null;
        state.LoadingFiles = false;
        // A just-created submission has no server files or sheet mappings — skip the pointless round-trips.
        state.FilesHydrated = true;
        state.SheetMappings = null;
        state.ClearingMappingRequestIDs = [];
        state.SheetMappingsHydrated = true;
      })
      .addCase(clearActiveSubmission, (state) => {
        state.UploadState = null;
        state.LoadingFiles = false;
        state.FilesHydrated = false;
        state.SheetMappings = null;
        state.ClearingMappingRequestIDs = [];
        state.SheetMappingsHydrated = false;
      })
      .addCase(saveSheetMappings.fulfilled, (state, action) => {
        state.SheetMappings = action.payload;
        state.SheetMappingsHydrated = true;
      })
      .addCase(fetchSheetMappings.fulfilled, (state, action) => {
        state.SheetMappings = action.payload;
        state.SheetMappingsHydrated = true;
        applyPersistedSheetMappingsToCsvFiles(state, action.meta.arg.worksheets);
      })
      .addCase(fetchSheetMappings.rejected, (state) => {
        // Marked hydrated even on failure so the effects don't refetch in a loop; associations
        // fall back to the auto-match, same as before sheet mapping persistence existed.
        state.SheetMappingsHydrated = true;
      })
      .addCase(clearWorksheetMapping.pending, (state, action) => {
        state.ClearingMappingRequestIDs = [...state.ClearingMappingRequestIDs, action.meta.requestId];
      })
      .addCase(clearWorksheetMapping.rejected, (state, action) => {
        state.ClearingMappingRequestIDs = state.ClearingMappingRequestIDs.filter(
          (requestID) => requestID !== action.meta.requestId
        );
      })
      .addCase(clearWorksheetMapping.fulfilled, (state, action) => {
        state.ClearingMappingRequestIDs = state.ClearingMappingRequestIDs.filter(
          (requestID) => requestID !== action.meta.requestId
        );
        if (!action.payload) return;
        for (const worksheet of action.meta.arg.worksheets) {
          for (const sheetMapping of findSheetMappingsForWorksheet(state.SheetMappings ?? [], worksheet)) {
            sheetMapping.columnMappingsConfirmed = false;
          }
        }
      })
      .addCase(fetchSubmissionFiles.pending, (state) => {
        state.LoadingFiles = true;
      })
      .addCase(fetchSubmissionFiles.fulfilled, (state, action) => {
        state.LoadingFiles = false;
        state.FilesHydrated = true;
        // The user already started a local batch while the fetch was in flight — don't clobber it.
        if (state.UploadState) return;

        // Re-uploads never overwrite server-side; the list is oldest first, so the last row per
        // file name is the current one.
        const latestByName = new Map<string, SubmissionFileModel>();
        for (const model of action.payload) {
          if (model.fileName) {
            latestByName.set(model.fileName, model);
          }
        }
        const models = [...latestByName.values()];
        if (models.length === 0) return;

        // Hydrated entries are metadata-only (no raw handle in submissionFileStore) — already scanned
        // server-side. The persisted association lives in sheetMappings (GetSheetMappings); the entry-level
        // suggestion is only a display default, so re-run the filename auto-suggest here.
        const mode = deriveUploadMode(models.map((model) => model.fileName ?? ""));
        state.UploadState = {
          mode,
          files: models.map((model) => {
            const targetWorksheet =
              mode === UploadMode.Csv ? suggestWorksheet(model.fileName ?? "", action.meta.arg.worksheets) : null;
            return {
              fileName: model.fileName ?? "",
              fileSizeBytes: model.fileSizeBytes ?? 0,
              status: UploadFileStatus.Scanned,
              submissionFileID: model.submissionFileID,
              targetWorksheet,
              matchedByFilename: targetWorksheet !== null,
            };
          }),
        };
        applyPersistedSheetMappingsToCsvFiles(state, action.meta.arg.worksheets);
      })
      .addCase(fetchSubmissionFiles.rejected, (state) => {
        state.LoadingFiles = false;
        // Marked hydrated even on failure so the effect doesn't refetch in a loop; the user can
        // still upload fresh files.
        state.FilesHydrated = true;
      })
      .addCase(uploadSubmissionFiles.fulfilled, (state, action) => {
        const modelsByName = new Map(action.payload.map((model) => [model.fileName, model]));
        for (const fileName of action.meta.arg.fileNames) {
          const file = state.UploadState?.files.find((item) => item.fileName === fileName);
          if (file) {
            file.status = UploadFileStatus.Scanned;
            file.submissionFileID = modelsByName.get(fileName)?.submissionFileID;
          }
        }
      })
      .addCase(uploadSubmissionFiles.rejected, (state, action) => {
        for (const fileName of action.meta.arg.fileNames) {
          const file = state.UploadState?.files.find((item) => item.fileName === fileName);
          if (file) {
            file.status = UploadFileStatus.Error;
            file.errorKind = UploadErrorKind.ScanFailed;
          }
        }
      });
  },
});

export const {
  setUploadFiles,
  setUploadFileWorksheet,
  setWorkbookSheets,
  setSheetWorksheets,
  setSheetKeyColumn,
  setMainSheet,
  clearUpload,
} = fileUploadSlice.actions;

// Hydrates the file list from the server on (re-)entry to the upload step. worksheets rides along
// so the fulfilled reducer can re-run the CSV filename auto-suggest.
export const fetchSubmissionFiles = createAsyncThunk(
  "DataImport/GetSubmissionFiles",
  async (arg: { submissionID: string; worksheets: string[] }) => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    return await dataImportService.getSubmissionFiles(arg.submissionID);
  }
);

// Thunk args stay serialisable: callers put the Files into submissionFileStore first and pass names
// here. The whole batch goes up as one UploadFiles request.
export const uploadSubmissionFiles = createAsyncThunk(
  "DataImport/UploadSubmissionFiles",
  async (arg: { submissionID: string; fileNames: string[] }, { rejectWithValue }) => {
    const files: File[] = [];
    for (const fileName of arg.fileNames) {
      const file = getSubmissionFile(arg.submissionID, fileName);
      if (!file) {
        return rejectWithValue(`No file selected for ${fileName}`);
      }
      files.push(file);
    }

    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    try {
      return await dataImportService.uploadSubmissionFiles(arg.submissionID, files);
    } catch (error) {
      // The rejected reducer only marks the rows errored — a lock takeover needs its own toast.
      if (error instanceof SubmissionLockError) {
        toast.error(error.message);
      }
      throw error;
    }
  }
);

// Persists the sheet-to-worksheet associations from the current UploadState. SaveSheetMappings replaces
// the submission's full sheet mapping selection server-side, so the complete set is always sent. Worksheet
// types come from the types slice, with a direct service fallback if that fetch failed on mount.
export const saveSheetMappings = createAsyncThunk(
  "DataImport/SaveSheetMappings",
  async (arg: { submissionID: string }, { getState }) => {
    const state = getState() as RootState;
    const uploadState = state.dataImportUpload.UploadState;
    const submission = selectActiveSubmission(state);
    if (!uploadState || !submission) throw new Error("No upload to save sheet mappings for");

    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    const worksheetTypes =
      state.dataImportTypes.WorksheetTypes[submission.templateType] ??
      (await dataImportService.getWorksheetTypes(TEMPLATE_TYPE_IDS[submission.templateType])).map(toWorksheetTypeEntry);

    const sheetMappings = buildSheetMappingsFromUploadState(uploadState, worksheetTypes);
    const saved = await dataImportService.saveSheetMappings(arg.submissionID, sheetMappings);
    return saved.map(toSheetMapping);
  }
);

// Hydrates the persisted sheet mapping selection on resume, restoring the sheet/file-to-worksheet picks
// made in an earlier session (mapping and validation also need the submissionSheetMappingID per
// worksheet). worksheets rides along so the fulfilled reducer can canonicalise the BE names.
export const fetchSheetMappings = createAsyncThunk(
  "DataImport/GetSheetMappings",
  async (arg: { submissionID: string; worksheets: string[] }) => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    const sheetMappings = await dataImportService.getSheetMappings(arg.submissionID);
    return sheetMappings.map(toSheetMapping);
  }
);

// Server-side soft delete. The entry is already gone from Redux when this fires (the component
// removes it via setUploadFiles), so no state change on fulfilled.
export const deleteSubmissionFile = createAsyncThunk(
  "DataImport/DeleteSubmissionFile",
  async (arg: { submissionFileID: string }) => {
    const dataImportService = container.get<IDataImportService>(SERVICE_TYPES.DataImportService);
    await dataImportService.deleteSubmissionFile(arg.submissionFileID);
  }
);

export const selectUploadState = (state: RootState) => state.dataImportUpload.UploadState;
export const selectSheetMappings = (state: RootState) => state.dataImportUpload.SheetMappings;
export const selectIsClearingMapping = (state: RootState) =>
  state.dataImportUpload.ClearingMappingRequestIDs.length > 0;
export const selectLoadingSubmissionFiles = (state: RootState) => state.dataImportUpload.LoadingFiles;
export const selectFilesHydrated = (state: RootState) => state.dataImportUpload.FilesHydrated;
export const selectSheetMappingsHydrated = (state: RootState) => state.dataImportUpload.SheetMappingsHydrated;

export default fileUploadSlice.reducer;
